import {otsBaseApi} from "../../ots-api";
import {AllocatableLoadSettings, AllocatableLoadSettingsPropagation} from "../../../model/allocatable-load-settings";
import {AllocatableLoadSettingsSearchCriteria} from "../../../model/search/allocatable-load-settings-search-criteria";
import {EntityIdentifier} from "../../../model/entity";

const ALL_TAG_TYPES = [
  "AllocatableLoadSettings",
  "ObjectAllocatableLoadSettings",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const allocatableLoadSettingsApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchAllocatableLoadSettings: builder.query<AllocatableLoadSettings[], AllocatableLoadSettingsSearchCriteria>({
      query: (arg) => ({
        url: `/allocatable-load-settings/search`,
        method: "POST",
        body: arg
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: 'AllocatableLoadSettings', id} as const)),
            {type: 'AllocatableLoadSettings', id: 'LIST'},
          ];
        } else {
          return [{type: 'AllocatableLoadSettings', id: 'LIST'}];
        }
      },
    }),
    getAllocatableLoadSettingsByObject: builder.query<AllocatableLoadSettings[], EntityIdentifier>({
      query: ({id, type}) => ({
        url: `/allocatable-load-settings/object/${type}/${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: 'AllocatableLoadSettings', id} as const)),
            {type: 'ObjectAllocatableLoadSettings', id: `${arg.type}:${arg.id}`},
          ];
        } else {
          return [{type: 'ObjectAllocatableLoadSettings', id: `${arg.type}:${arg.id}`}]
        }
      },
    }),
    getAllocatableLoadSettings: builder.query<AllocatableLoadSettings, string>({
      query: (id) => ({
        url: `/allocatable-load-settings/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{type: 'AllocatableLoadSettings', id}],
    }),
    createAllocatableLoadSettings: builder.mutation<AllocatableLoadSettings, Partial<AllocatableLoadSettings>>({
      query: (allocatableLoadSettings) => ({
        url: '/allocatable-load-settings',
        method: 'POST',
        body: allocatableLoadSettings,
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: { type: TagType, id?: string }[] = []
        tags.push({type: 'AllocatableLoadSettings', id: 'LIST'})
        if (result) {
          tags.push({type: 'ObjectAllocatableLoadSettings', id: `${arg.objectType}:${arg.objectId}`})
        }
        return tags
      }
    }),
    updateAllocatableLoadSettings: builder.mutation<AllocatableLoadSettings, Partial<AllocatableLoadSettings>>({
      query: (allocatableLoadSettings) => ({
        url: '/allocatable-load-settings',
        method: 'PUT',
        body: allocatableLoadSettings,
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: { type: TagType, id?: string }[] = []
        tags.push({type: 'AllocatableLoadSettings', id: 'LIST'})
        if (result) {
          tags.push({type: 'ObjectAllocatableLoadSettings', id: `${arg.objectType}:${arg.objectId}`})
        }
        return tags
      }
    }),
    propagateAllocatableLoadSettings: builder.mutation<AllocatableLoadSettings, {
      id: string,
      propagation: AllocatableLoadSettingsPropagation
    }>({
      query: ({id, propagation}) => ({
        url: `/allocatable-load-settings/${id}/propagate`,
        method: 'POST',
        body: propagation,
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: { type: TagType, id?: string }[] = []
        tags.push({type: 'AllocatableLoadSettings'})
        tags.push({type: 'ObjectAllocatableLoadSettings'})
        return tags
      }
    }),
    deleteAllocatableLoadSettings: builder.mutation<void, string>({
      query: (id) => ({
        url: `/allocatable-load-settings/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) =>
        [
          {type: 'AllocatableLoadSettings', arg},
          {type: 'ObjectAllocatableLoadSettings'},
        ],
    }),
  }),
})

export const {
  useSearchAllocatableLoadSettingsQuery,
  useGetAllocatableLoadSettingsByObjectQuery,
  useGetAllocatableLoadSettingsQuery,
  useCreateAllocatableLoadSettingsMutation,
  useUpdateAllocatableLoadSettingsMutation,
  usePropagateAllocatableLoadSettingsMutation,
  useDeleteAllocatableLoadSettingsMutation,
} = allocatableLoadSettingsApi;
